import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { ApiError, type AdminApi } from "@/api";
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";
import { domainLabel } from "@/components/test-cases/allTestCasesSearch";
import { cn } from "@/lib/utils";
import type { ReferenceJob, ReferenceOptions, ReferenceVersionList, TestCaseListItem } from "@/types";
import { DomainChip, ReviewBadge } from "./ReferenceBadges";
import { ReferenceVersionViewer } from "./ReferenceVersionViewer";
import { reviewStatus } from "./referenceReview";
import {
  DEFAULT_PAGE_SIZE,
  DOMAIN_FILTER_OPTIONS,
  MINUTES_PER_CASE,
  PAGE_SIZES,
  STATUS_FILTERS,
  STATUS_LABEL,
  VERSION_READ_CONCURRENCY,
  blockedReason,
  caseLabel,
  countByFilter,
  domainKey,
  domainOrder,
  groupedOrder,
  isPickable,
  kstDate,
  kstShort,
  mapLimited,
  matchesStatus,
  minutesSince,
  pageNumbers,
  pageOf,
  rememberJobIds,
  rememberedJobIds,
  rowStatus,
  startTargets,
  type RowStatus,
  type StatusFilter,
} from "./referenceCases";

export const POLL_MS = 5000;
/** Requests sent at once when several TCs start together; SAMS's worker queues the rest. */
const START_CONCURRENCY = 2;
const LIST_PAGE = 100;
const LIST_PAGES_MAX = 50;

type RequestFailure = { key: string; test_case_id: string; code: string; message: string; at: string };

const errorText = (err: unknown) =>
  err instanceof ApiError && err.code ? `${err.message} (${err.code})` : err instanceof Error ? err.message : String(err);

const STATUS_BADGE: Record<RowStatus, string> = {
  none: "",
  unknown: "",
  done: "border-success-line bg-success-surface text-success",
  queued: "",
  running: "border-accent-line bg-accent text-accent-foreground",
  failed: "border-destructive-line bg-destructive-surface text-destructive",
  blocked: "text-muted-foreground",
};

function StatusBadge({ status }: { status: RowStatus }) {
  return (
    <Badge size="compact" className={STATUS_BADGE[status]}>
      {(status === "running" || status === "unknown") && <Loader2 className="animate-spin" aria-hidden />}
      {STATUS_LABEL[status]}
    </Badge>
  );
}

/** Every TC, retired ones too, read page by page. */
async function listAllCases(api: AdminApi) {
  const items: TestCaseListItem[] = [];
  let loadErrors: Record<string, string> = {};
  let cursor: string | null = null;
  for (let n = 0; n < LIST_PAGES_MAX; n += 1) {
    const page = await api.listTestCases({ cursor, limit: LIST_PAGE, activeOnly: false });
    items.push(...(page.items ?? []));
    if (page.load_errors) loadErrors = { ...loadErrors, ...page.load_errors };
    cursor = page.next_cursor ?? null;
    if (!cursor) break;
  }
  return { items, loadErrors: Object.keys(loadErrors).length ? loadErrors : null };
}

/**
 * Test Cases → Reference Answer → 생성. Pick one TC or many, regenerate their
 * Reference Answer, and follow each run. Contract:
 * base_agent_replay_lab/docs/sams-reference-api.md — one POST per TC, a job
 * per POST, polled until it settles; each success is the TC's new version,
 * waiting on the 검토 tab before scoring reads it.
 */
export function ReferenceGenerateTab({
  api,
  reloadKey,
  prodAccess,
  onOpenReview,
}: {
  api: AdminApi;
  reloadKey: number;
  /** Null while SAMS has not said; prod TCs are not held back then. */
  prodAccess: boolean | null;
  /** Opens a TC's version on the 검토 tab. */
  onOpenReview: (tc: string, snap: string | null) => void;
}) {
  const [cases, setCases] = useState<TestCaseListItem[] | null>(null);
  const [listError, setListError] = useState("");
  const [sourceErrors, setSourceErrors] = useState<Record<string, string> | null>(null);
  const [options, setOptions] = useState<ReferenceOptions | null>(null);
  const [versions, setVersions] = useState<Record<string, ReferenceVersionList>>({});
  const [versionErrors, setVersionErrors] = useState<Record<string, string>>({});
  const [jobs, setJobs] = useState<Record<string, ReferenceJob>>({});
  const [failures, setFailures] = useState<RequestFailure[]>([]);
  const [now, setNow] = useState(() => Date.now());

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const [focusSnap, setFocusSnap] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("");
  const [env, setEnv] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [regenerate, setRegenerate] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [actionError, setActionError] = useState("");

  const today = kstDate(now);
  const mergeJobs = useCallback((list: readonly ReferenceJob[]) => {
    if (!list.length) return;
    setJobs((current) => {
      const next = { ...current };
      for (const job of list) next[job.job_id] = job;
      return next;
    });
  }, []);

  const readVersions = useCallback(
    async (ids: readonly string[]) => {
      const results = await mapLimited(ids, VERSION_READ_CONCURRENCY, async (id) => {
        try {
          return { id, list: await api.listReferenceVersions(id), error: "" };
        } catch (err) {
          return { id, list: null, error: errorText(err) };
        }
      });
      setVersions((current) => {
        const next = { ...current };
        for (const r of results) if (r.list) next[r.id] = r.list;
        return next;
      });
      setVersionErrors((current) => {
        const next = { ...current };
        for (const r of results) {
          if (r.list) delete next[r.id];
          else next[r.id] = r.error;
        }
        return next;
      });
      mergeJobs(results.flatMap((r) => (r.list?.open_job ? [r.list.open_job] : [])));
    },
    [api, mergeJobs],
  );

  // --- Load ----------------------------------------------------------------
  useEffect(() => {
    let current = true;
    setListError("");
    void (async () => {
      try {
        const { items, loadErrors } = await listAllCases(api);
        if (!current) return;
        setCases(items);
        setSourceErrors(loadErrors);
        setFocus((f) => f ?? items[0]?.test_case_id ?? null);
        void readVersions(items.map((tc) => tc.test_case_id));
      } catch (err) {
        if (current) setListError(errorText(err));
      }
    })();
    void api.getReferenceOptions().then(
      (o) => current && setOptions(o),
      () => current && setOptions(null),
    );
    // The jobs this browser started: one read each; a gone job (404 after 7 days) is forgotten.
    void (async () => {
      const ids = rememberedJobIds();
      const found = await mapLimited(ids, VERSION_READ_CONCURRENCY, (id) =>
        api.getReferenceJob(id).then(
          (job) => job,
          (err) => (err instanceof ApiError && err.status === 404 ? null : undefined),
        ),
      );
      if (!current) return;
      mergeJobs(found.filter((job): job is ReferenceJob => Boolean(job)));
      rememberJobIds(ids.filter((_, i) => found[i] !== null));
    })();
    return () => {
      current = false;
    };
  }, [api, reloadKey, readVersions, mergeJobs]);

  // --- Poll open jobs ------------------------------------------------------
  const openIds = useMemo(
    () =>
      Object.values(jobs)
        .filter((j) => j.status === "queued" || j.status === "running")
        .map((j) => j.job_id)
        .sort()
        .join(","),
    [jobs],
  );
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);
  useEffect(() => {
    if (!openIds) return;
    let stopped = false;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void (async () => {
        const ids = openIds.split(",");
        const polled = await Promise.all(ids.map((id) => api.getReferenceJob(id).catch(() => null)));
        if (stopped) return;
        // A missed poll is not a failed job — the next tick asks again.
        const settled = polled.filter(
          (job): job is ReferenceJob => Boolean(job) && jobsRef.current[job!.job_id]?.status !== job!.status,
        );
        mergeJobs(polled.filter((job): job is ReferenceJob => Boolean(job)));
        const done = settled.filter((job) => job.status === "succeeded").map((job) => job.test_case_id);
        if (done.length) void readVersions(done);
      })();
    }, POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [api, openIds, mergeJobs, readVersions]);

  // Elapsed minutes and "today" move even when nothing is polled.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  // --- Derived ----------------------------------------------------------------
  const newestJob = useMemo(() => {
    const byTc = new Map<string, ReferenceJob>();
    for (const job of Object.values(jobs)) {
      const seen = byTc.get(job.test_case_id);
      if (!seen || job.created_at > seen.created_at) byTc.set(job.test_case_id, job);
    }
    return byTc;
  }, [jobs]);

  const rows = useMemo(
    () =>
      (cases ?? []).map((tc) => {
        const blocked = blockedReason(tc, prodAccess ?? true);
        const status = rowStatus({ blocked, versions: versions[tc.test_case_id], job: newestJob.get(tc.test_case_id), today });
        return { tc, blocked, status };
      }),
    [cases, prodAccess, versions, newestJob, today],
  );
  const statusOf = useMemo(() => {
    const map = new Map(rows.map((r) => [r.tc.test_case_id, r.status]));
    return (id: string) => map.get(id) ?? "blocked";
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return groupedOrder(
      rows
        .filter(({ tc, status }) =>
          (!env || tc.source_environment === env) &&
          (!domain || domainKey(tc) === domain) &&
          matchesStatus(status, statusFilter) &&
          (!needle ||
            caseLabel(tc).toLowerCase().includes(needle) ||
            (tc.trigger_preview ?? "").toLowerCase().includes(needle) ||
            tc.test_case_id.startsWith(needle)),
        )
        .map((r) => ({ ...r, domain: r.tc.domain })),
    );
  }, [rows, q, env, domain, statusFilter]);

  const counts = useMemo(() => countByFilter(rows.map((r) => r.status)), [rows]);
  const slice = pageOf(filtered, page, size);
  const pagePickable = slice.rows.filter((r) => isPickable(r.status));
  const pageAllOn = pagePickable.length > 0 && pagePickable.every((r) => selected.has(r.tc.test_case_id));
  const allPickable = filtered.filter((r) => isPickable(r.status));
  const everyOn = allPickable.length > 0 && allPickable.every((r) => selected.has(r.tc.test_case_id));

  const targets = useMemo(
    () => startTargets(selected, cases ?? [], statusOf, regenerate),
    [selected, cases, statusOf, regenerate],
  );

  const versionFailures = Object.keys(versionErrors).length;
  const firstVersionError = Object.values(versionErrors)[0];

  // --- Actions ------------------------------------------------------------------
  const resetPage = () => setPage(1);
  const toggle = (ids: readonly string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const request = async (ids: readonly string[]) => {
    setActionError("");
    const started: ReferenceJob[] = [];
    const failed: RequestFailure[] = [];
    await mapLimited(ids, START_CONCURRENCY, async (id) => {
      try {
        started.push(await api.requestReference(id));
      } catch (err) {
        // Someone (or an earlier click) is already running this TC: follow that job.
        const running = err instanceof ApiError && err.code === "REFERENCE_JOB_ACTIVE" ? err.detail.job_id : null;
        if (typeof running === "string") {
          const job = await api.getReferenceJob(running).catch(() => null);
          if (job) {
            started.push(job);
            return;
          }
        }
        failed.push({
          key: `${id}-${Date.now()}`,
          test_case_id: id,
          code: err instanceof ApiError ? err.code : "",
          message: err instanceof Error ? err.message : String(err),
          at: new Date().toISOString(),
        });
      }
    });
    mergeJobs(started);
    rememberJobIds([...started.map((j) => j.job_id), ...rememberedJobIds()]);
    setFailures((current) => [...failed, ...current.filter((f) => !ids.includes(f.test_case_id))]);
    toggle(started.map((j) => j.test_case_id), false);
    if (failed.length && !started.length) setActionError(`요청하지 못했습니다: ${failed[0].message}`);
  };

  const start = async () => {
    setStarting(true);
    try {
      await request(targets.run.map((tc) => tc.test_case_id));
    } finally {
      setStarting(false);
      setConfirming(false);
    }
  };

  // --- Progress panel -------------------------------------------------------------
  const caseById = useMemo(() => new Map((cases ?? []).map((tc) => [tc.test_case_id, tc])), [cases]);
  const nameOf = (id: string) => {
    const tc = caseById.get(id);
    return tc ? caseLabel(tc) : id.slice(0, 8);
  };
  const shownJobs = Object.values(jobs)
    .filter((j) => j.status === "queued" || j.status === "running" || kstDate(j.created_at) === today)
    .sort((a, b) => {
      const rank = (j: ReferenceJob) => (j.status === "running" ? 0 : j.status === "queued" ? 1 : 2);
      return rank(a) - rank(b) || (b.finished_at ?? b.created_at).localeCompare(a.finished_at ?? a.created_at);
    });
  const succeeded = shownJobs.filter((j) => j.status === "succeeded").length;
  const failedJobs = shownJobs.filter((j) => j.status === "failed").length + failures.length;
  const remaining = shownJobs.filter((j) => j.status === "queued" || j.status === "running").length;

  const focusCase = focus ? caseById.get(focus) ?? null : null;
  const focusVersions = focus ? versions[focus] : undefined;
  const focusRow = focus ? rows.find((r) => r.tc.test_case_id === focus) : undefined;

  // --- Render ---------------------------------------------------------------------
  if (listError) return <Banner tone="error">TC 목록을 읽지 못했습니다: {listError}</Banner>;
  if (!cases) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> TC 목록을 읽는 중…
      </p>
    );
  }

  const groupKeys = domainOrder(slice.rows.map((r) => r.tc));

  return (
    <div className="grid gap-4.5 pb-20">
      {sourceErrors && Object.keys(sourceErrors).length > 0 && (
        <Banner tone="error">
          {Object.keys(sourceErrors).join(", ")} 목록을 읽지 못해 일부 TC가 빠졌습니다.
        </Banner>
      )}
      {versionFailures > 0 && (
        <Banner tone="error">
          {versionFailures}개 TC의 Reference 버전을 읽지 못했습니다: {firstVersionError}
        </Banner>
      )}
      {actionError && <Banner tone="error">{actionError}</Banner>}

      <div className="flex flex-wrap gap-2" role="group" aria-label="상태로 거르기">
        {STATUS_FILTERS.map(({ value, label }) => (
          <button
            key={value || "all"}
            type="button"
            aria-pressed={statusFilter === value}
            onClick={() => {
              setStatusFilter(value);
              resetPage();
            }}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[12.5px]",
              statusFilter === value && "border-primary bg-accent text-accent-foreground",
            )}
          >
            {label} <b className="font-semibold tabular-nums">{counts[value]}</b>
          </button>
        ))}
      </div>

      <div className="grid items-start gap-4.5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
            <Input
              type="search"
              aria-label="찾기"
              placeholder="질문 또는 TC ID로 찾기"
              className="min-w-0 flex-[1_1_220px]"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                resetPage();
              }}
            />
            <select
              aria-label="Category"
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                resetPage();
              }}
              className="rounded-md border border-border bg-surface-input px-2 py-1.5 text-[12.5px]"
            >
              <option value="">Category 전체</option>
              {DOMAIN_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              aria-label="환경"
              value={env}
              onChange={(e) => {
                setEnv(e.target.value);
                resetPage();
              }}
              className="rounded-md border border-border bg-surface-input px-2 py-1.5 text-[12.5px]"
            >
              <option value="">환경 전체</option>
              <option value="dev">dev</option>
              <option value="prod">prod</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              disabled={pagePickable.length === 0}
              onClick={() => toggle(pagePickable.map((r) => r.tc.test_case_id), !pageAllOn)}
            >
              {pageAllOn ? "이 페이지 선택 해제" : `이 페이지 전체 선택 (${pagePickable.length})`}
            </Button>
            {slice.pages > 1 && allPickable.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-primary"
                onClick={() => toggle(allPickable.map((r) => r.tc.test_case_id), !everyOn)}
              >
                {everyOn ? `조건에 맞는 ${allPickable.length}개 모두 해제` : `조건에 맞는 ${allPickable.length}개 모두 선택`}
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border text-left text-[11.5px] text-muted-foreground">
                  <th className="w-9 px-2.5 py-2 font-medium">
                    <span className="sr-only">선택</span>
                  </th>
                  <th className="px-2.5 py-2 font-medium">질문</th>
                  <th className="px-2.5 py-2 font-medium max-md:hidden">Category</th>
                  <th className="px-2.5 py-2 font-medium">상태</th>
                  <th className="px-2.5 py-2 font-medium whitespace-nowrap max-md:hidden">최근 Reference</th>
                  <th className="px-2.5 py-2 font-medium">검토</th>
                </tr>
              </thead>
              <tbody>
                {slice.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-5 text-muted-foreground">
                      조건에 맞는 TC가 없습니다.
                    </td>
                  </tr>
                )}
                {groupKeys.map((key) => {
                  const here = slice.rows.filter((r) => domainKey(r.tc) === key);
                  // The header box covers the whole Category under the current filters, not this page alone.
                  const members = filtered.filter((r) => domainKey(r.tc) === key);
                  const pick = members.filter((r) => isPickable(r.status)).map((r) => r.tc.test_case_id);
                  const on = pick.filter((id) => selected.has(id)).length;
                  const label = domainLabel(key);
                  return [
                    <tr key={`g-${key}`} className="border-b border-border bg-muted">
                      <td className="px-2.5 py-1.5">
                        <input
                          type="checkbox"
                          className="size-3.75 accent-primary"
                          aria-label={`${label} 전체 ${members.length}개 선택`}
                          disabled={pick.length === 0}
                          checked={pick.length > 0 && on === pick.length}
                          ref={(el) => {
                            if (el) el.indeterminate = on > 0 && on < pick.length;
                          }}
                          onChange={(e) => toggle(pick, e.target.checked)}
                        />
                      </td>
                      <td colSpan={5} className="px-2.5 py-1.5 text-xs font-semibold text-accent-foreground">
                        {label}
                        {members[0] !== here[0] && " (이어서)"}
                        <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
                          전체 {members.length}개 · {on}개 선택
                        </span>
                      </td>
                    </tr>,
                    ...here.map(({ tc, status, blocked }) => {
                      const versionList = versions[tc.test_case_id];
                      const latest = versionList?.items[0];
                      const job = newestJob.get(tc.test_case_id);
                      const isSelected = selected.has(tc.test_case_id);
                      return (
                        <tr
                          key={tc.test_case_id}
                          className={cn(
                            "border-b border-border last:border-0 hover:bg-accent-hover",
                            isSelected && "bg-accent",
                            blocked && "text-muted-foreground",
                          )}
                        >
                          <td className="px-2.5 py-2">
                            <input
                              type="checkbox"
                              className="size-3.75 accent-primary"
                              aria-label={`${caseLabel(tc)} 선택`}
                              checked={isSelected}
                              disabled={!isPickable(status)}
                              onChange={(e) => {
                                toggle([tc.test_case_id], e.target.checked);
                                setFocus(tc.test_case_id);
                                setFocusSnap(null);
                              }}
                            />
                          </td>
                          <td className="max-w-0 px-2.5 py-2 md:w-[46%]">
                            <button
                              type="button"
                              className="block w-full truncate text-left hover:underline"
                              title={tc.trigger_preview ?? undefined}
                              onClick={() => {
                                setFocus(tc.test_case_id);
                                setFocusSnap(null);
                              }}
                            >
                              {caseLabel(tc)}
                            </button>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {tc.test_case_id.slice(0, 8)}
                            </span>
                            <span
                              className={cn(
                                "ml-1 rounded border px-1 text-[10.5px]",
                                tc.source_environment === "prod"
                                  ? "border-destructive/40 text-destructive"
                                  : "border-border text-muted-foreground",
                              )}
                            >
                              {tc.source_environment}
                            </span>
                          </td>
                          <td className="px-2.5 py-2 max-md:hidden">
                            <DomainChip domain={tc.domain} />
                          </td>
                          <td className="px-2.5 py-2">
                            <StatusBadge status={status} />
                            {blocked && <div className="mt-0.5 text-[11px] text-muted-foreground">{blocked}</div>}
                            {status === "failed" && job?.error && (
                              <div className="mt-0.5 text-[11px] text-destructive">{job.error.code}</div>
                            )}
                          </td>
                          <td className="px-2.5 py-2 whitespace-nowrap text-muted-foreground tabular-nums max-md:hidden">
                            {latest ? `v${latest.version} · ${kstShort(latest.created_at)}` : versionList ? "없음" : "—"}
                          </td>
                          <td className="px-2.5 py-2">{latest ? <ReviewBadge status={reviewStatus(latest)} /> : <span className="text-muted-foreground">—</span>}</td>
                        </tr>
                      );
                    }),
                  ];
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t border-border px-3 py-2.5 text-[12.5px] text-muted-foreground">
            <span className="tabular-nums">
              {filtered.length ? `${slice.from + 1}–${slice.from + slice.rows.length} / ${filtered.length}개` : "0개"}
            </span>
            <label className="flex items-center gap-1">
              한 페이지에
              <select
                aria-label="한 페이지에 보일 개수"
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  resetPage();
                }}
                className="rounded-md border border-border bg-surface-input px-1.5 py-0.5"
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}개
                  </option>
                ))}
              </select>
            </label>
            <nav className="ml-auto flex items-center gap-1 tabular-nums" aria-label="페이지">
              <Button variant="outline" size="icon-sm" aria-label="이전 페이지" disabled={slice.page === 1} onClick={() => setPage(slice.page - 1)}>
                ‹
              </Button>
              {pageNumbers(slice.page, slice.pages).map((n, i) =>
                n === "gap" ? (
                  <span key={`gap-${i}`} className="px-0.5">
                    …
                  </span>
                ) : (
                  <Button
                    key={n}
                    variant={n === slice.page ? "default" : "outline"}
                    size="icon-sm"
                    aria-current={n === slice.page ? "page" : undefined}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </Button>
                ),
              )}
              <Button variant="outline" size="icon-sm" aria-label="다음 페이지" disabled={slice.page === slice.pages} onClick={() => setPage(slice.page + 1)}>
                ›
              </Button>
            </nav>
          </div>
        </Card>

        <aside className="grid min-w-0 gap-3.5">
          <Card>
            <h2 className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-3 text-[13.5px] font-semibold">
              진행 현황
              {(shownJobs.length > 0 || failures.length > 0) && (
                <small className="text-[11.5px] font-normal text-muted-foreground tabular-nums">
                  완료 {succeeded} · 실패 {failedJobs} · 남음 {remaining}
                </small>
              )}
            </h2>
            <ul className="grid py-1.5" aria-label="진행 현황">
              {shownJobs.length === 0 && failures.length === 0 && (
                <li className="px-3.5 py-3 text-[12.5px] text-muted-foreground">
                  오늘 실행한 것이 없습니다. TC를 고르고 "Reference 생성"을 누르세요.
                </li>
              )}
              {failures.map((f) => (
                <li key={f.key} className="grid gap-0.5 border-b border-dashed border-border px-3.5 py-2 text-[12.5px] last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">{nameOf(f.test_case_id)}</span>
                    <StatusBadge status="failed" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11.5px] text-destructive">요청 거부: {f.message}</span>
                    <Button variant="ghost" size="sm" className="h-auto min-h-0 px-1 py-0 text-primary" onClick={() => void request([f.test_case_id])}>
                      다시 시도
                    </Button>
                  </div>
                </li>
              ))}
              {shownJobs.map((job) => (
                <li key={job.job_id} className="grid gap-0.5 border-b border-dashed border-border px-3.5 py-2 text-[12.5px] last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate" title={nameOf(job.test_case_id)}>
                      {nameOf(job.test_case_id)}
                    </span>
                    {job.status === "running" ? (
                      <Badge size="compact" className={STATUS_BADGE.running}>
                        <Loader2 className="animate-spin" aria-hidden />
                        {minutesSince(job.started_at, now)}분 경과
                      </Badge>
                    ) : job.status === "queued" ? (
                      <Badge size="compact">대기</Badge>
                    ) : job.status === "succeeded" ? (
                      <Badge size="compact" className={STATUS_BADGE.done}>
                        저장 v{job.result?.snapshot?.version ?? "?"} · 미통과
                      </Badge>
                    ) : (
                      <StatusBadge status="failed" />
                    )}
                  </div>
                  {job.status === "failed" && (
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 text-[11.5px] break-words text-destructive">
                        {job.error ? `${job.error.message} (${job.error.code})` : "실패"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto min-h-0 shrink-0 px-1 py-0 text-primary"
                        onClick={() => void request([job.test_case_id])}
                      >
                        다시 시도
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <h2 className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-3 text-[13.5px] font-semibold">
              TC 상세 <small className="text-[11.5px] font-normal text-muted-foreground">질문을 누르면 바뀜</small>
            </h2>
            <div className="grid gap-2.5 px-3.5 py-3 text-[12.5px]">
              {!focusCase ? (
                <p className="text-muted-foreground">질문을 누르면 버전 기록이 보입니다.</p>
              ) : (
                <>
                  <p className="text-[13px] font-semibold">{caseLabel(focusCase)}</p>
                  <p className="font-mono text-[11px] break-all text-muted-foreground">
                    {focusCase.test_case_id} · {focusCase.source_environment}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <DomainChip domain={focusCase.domain} />
                    {focusRow && <StatusBadge status={focusRow.status} />}
                    {focusRow?.blocked && <span className="text-[11px] text-muted-foreground">{focusRow.blocked}</span>}
                  </div>
                  {!focusVersions ? (
                    <p className="text-muted-foreground">{versionErrors[focusCase.test_case_id] ?? "버전을 읽는 중…"}</p>
                  ) : focusVersions.items.length === 0 ? (
                    <p className="rounded-md bg-muted px-2.5 py-2 text-muted-foreground">아직 만든 Reference가 없습니다.</p>
                  ) : (
                    <>
                      <ReferenceVersionViewer
                        versions={focusVersions}
                        selectedId={focusSnap}
                        onSelect={setFocusSnap}
                        bodyClassName="max-h-56"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-self-start"
                        onClick={() => onOpenReview(focusCase.test_case_id, focusSnap ?? focusVersions.items[0]?.snapshot_id ?? null)}
                      >
                        검토 탭에서 열기
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </Card>

          <Card>
            <h2 className="border-b border-border px-3.5 py-3 text-[13.5px] font-semibold">생성 규칙</h2>
            <ol className="grid list-decimal gap-1 py-2.5 pr-3.5 pl-7.5 text-xs text-muted-foreground">
              <li>오늘(KST) 이미 만든 TC는 건너뛴다. 다시 만들려면 아래 "오늘 것도 다시 만들기"를 켠다.</li>
              <li>만들 때마다 새 버전이 추가된다. 검토 탭에서 통과해야 채점 기준이 되고, 그전까지는 이전에 통과한 버전으로 채점한다. 이전 버전은 남는다.</li>
              <li>여러 개를 고르면 SAMS가 차례로 처리한다. 한 건은 보통 수 분, 최대 10분 걸린다.</li>
              <li>비활성이거나 탈락한 TC는 생성 불가로 표시되고 고를 수 없다.</li>
              <li>prod TC는 prod 권한이 있는 관리자만 만들 수 있다.</li>
            </ol>
          </Card>
        </aside>
      </div>

      <div className="sticky bottom-0 z-10 -mx-6 flex flex-wrap items-center gap-x-4 gap-y-2.5 border-t border-border bg-card px-6 py-3 shadow-[0_-6px_18px_rgb(31_27_45/6%)]">
        <span className="font-semibold tabular-nums">{selected.size}개 선택</span>
        {targets.run.length > 0 && (
          <span className="text-[12.5px] text-muted-foreground tabular-nums">
            {targets.run.length}개 · TC당 약 {MINUTES_PER_CASE}분
          </span>
        )}
        {targets.skip.length > 0 && (
          <span className="text-xs text-accent-foreground">오늘 이미 만든 {targets.skip.length}개는 건너뜀</span>
        )}
        <span className="flex-auto" />
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12.5px]">
          <input
            type="checkbox"
            className="size-3.75 accent-primary"
            checked={regenerate}
            onChange={(e) => setRegenerate(e.target.checked)}
          />
          오늘 것도 다시 만들기
        </label>
        <Button variant="outline" size="sm" disabled={selected.size === 0} onClick={() => setSelected(new Set())}>
          선택 해제
        </Button>
        <Button size="sm" disabled={targets.run.length === 0 || starting} onClick={() => setConfirming(true)}>
          {targets.run.length ? `Reference 생성 (${targets.run.length})` : "Reference 생성"}
        </Button>
      </div>

      <Dialog open={confirming} onOpenChange={(open: boolean) => !starting && setConfirming(open)}>
        <DialogContent className="grid max-h-[calc(100vh-56px)] w-[min(520px,calc(100vw-32px))] gap-3 overflow-y-auto">
          <DialogTitle>Reference {targets.run.length}개를 만듭니다</DialogTitle>
          <DialogDescription className="sr-only">생성할 TC와 설정을 확인합니다.</DialogDescription>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 text-[12.5px]">
            <dt className="text-muted-foreground">예상 시간</dt>
            <dd>TC당 약 {MINUTES_PER_CASE}분 (최대 10분), SAMS가 차례로 처리</dd>
            <dt className="text-muted-foreground">모델</dt>
            <dd>
              {options ? options.default_model : "서버 기본 모델"} · 추론 강도 {options?.reasoning_effort.default ?? "max"}
            </dd>
            <dt className="text-muted-foreground">저장</dt>
            <dd>TC마다 새 버전으로 추가됩니다. 검토 탭에서 통과해야 채점 기준이 됩니다 (이전 버전은 남음)</dd>
            {targets.skip.length > 0 && (
              <>
                <dt className="text-muted-foreground">건너뜀</dt>
                <dd>오늘 이미 만든 {targets.skip.length}개</dd>
              </>
            )}
            {targets.run.some((tc) => tc.source_environment === "prod") && (
              <>
                <dt className="text-muted-foreground">prod</dt>
                <dd className="text-destructive">
                  prod TC {targets.run.filter((tc) => tc.source_environment === "prod").length}개 포함
                </dd>
              </>
            )}
          </dl>
          {options && !options.default_model_allowed && (
            <Banner tone="error">기본 모델이 SAMS 모델 목록에 없어 서버가 요청을 거부합니다 (REFERENCE_DEFAULT_MODEL_UNAVAILABLE).</Banner>
          )}
          <ul className="grid max-h-56 list-disc gap-0.5 overflow-y-auto pl-4.5 text-[12.5px]">
            {targets.run.map((tc) => (
              <li key={tc.test_case_id}>
                {caseLabel(tc)} <span className="font-mono text-[11px] text-muted-foreground">{tc.test_case_id.slice(0, 8)}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={starting} onClick={() => setConfirming(false)}>
              취소
            </Button>
            <Button disabled={starting} onClick={() => void start()}>
              {starting && <Loader2 className="animate-spin" />}
              생성 시작
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
